import React, { useState, useEffect } from 'react';
import axios from 'axios';
import swal from 'sweetalert';
import { Modal } from 'react-bootstrap';

import { getCookie } from '../common_component/cookie.js';
import PageError from '../common_component/PageError.js';

const formatTypes = [
    { value: "pdf_type", label: "PDF" },
    { value: "bill_type", label: "Bill" },
];

// fills template placeholders ({{name}} / $name) with the template's sample_data for preview
// longer placeholders first so "$part_10" is not broken by "$part_1"
const fillSampleData = (template) => {
    const sampleData = [...(template.sample_data || [])].sort((a, b) => b.placeholder.length - a.placeholder.length);
    return sampleData.reduce((html, x) => html.split(x.placeholder).join(x.value), template.template_content || "");
};

// Shows organization / default templates of a category and lets the organization select one
function TransactionTemplateSettings({ organization_id, page_name, write, category_type = "customer_invoice" }) {
    const [state, setState] = useState(2);
    const [formatType, setFormatType] = useState("pdf_type");
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [savingTemplateId, setSavingTemplateId] = useState(null);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    const headers = {
        headers: {
            "Content-Type": "application/json",
            "Accept": "*/*",
            "token": getCookie("access_token")
        }
    }

    const getTemplateOptions = (format_type) => {
        setState(2);
        axios.post(`${process.env.REACT_APP_SERVER_URL}/get-transaction-template-options`, { category_type: category_type, format_type: format_type, organization_id: organization_id, page_name: page_name }, headers).then((res) => {
            if (res.data.operation === "success") {
                setTemplates(res.data.data.templates);
                setSelectedTemplateId(res.data.data.selected_template_id);
                setState(1);
            }
            else {
                setState(0);
            }
        })
            .catch((error) => {
                console.log(error);
                setState(0);
            });
    }

    useEffect(() => {
        if (organization_id) {
            getTemplateOptions(formatType);
        }
    }, [organization_id, formatType])

    const selectTemplate = (template) => {
        swal({
            title: "Use this template?",
            text: `"${template.template_name}" will be used for all ${formatTypes.find(x => x.value === formatType).label} documents.`,
            icon: "warning",
            buttons: ["Cancel", "Use Template"],
        }).then((confirm) => {
            if (!confirm) return;

            setSavingTemplateId(template.template_id);
            axios.post(`${process.env.REACT_APP_SERVER_URL}/save-transaction-template-selection`, { category_type: category_type, format_type: formatType, template_id: template.template_id, organization_id: organization_id, page_name: page_name }, headers).then((res) => {
                setSavingTemplateId(null);
                if (res.data.operation === "success") {
                    setSelectedTemplateId(template.template_id);
                    swal("Great!", res.data.message, "success");
                }
                else {
                    swal("Oops!", res.data.message, "error");
                }
            })
                .catch((error) => {
                    setSavingTemplateId(null);
                    console.log(error);
                    swal("Oops!", "Unable to save template selection", "error");
                });
        });
    }

    return (
        <div>
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-6 gap-3">
                <div className="text-muted fs-6">Select the template used for customer invoices. If none is selected, the default template is used.</div>
                <div className="btn-group">
                    {formatTypes.map(x => (
                        <button key={x.value} className={`btn btn-sm ${formatType === x.value ? "btn-primary" : "btn-light"}`} onClick={() => setFormatType(x.value)}>{x.label}</button>
                    ))}
                </div>
            </div>

            {
                state == 1 ?
                    templates.length === 0 ?
                        <PageError message='No templates available' showReloadMsg={false} />
                        :
                        <div className="row g-6">
                            {templates.map((template) => {
                                const isSelected = template.template_id === selectedTemplateId;
                                return (
                                    <div className="col-md-6 col-xl-4" key={template.template_id}>
                                        <div className={`card h-100 border ${isSelected ? "border-primary border-2" : "border-gray-300"}`}>
                                            <div className="card-header align-items-center px-5" style={{ minHeight: "56px" }}>
                                                <div className="card-title flex-column">
                                                    <span className="fw-bold fs-6">{template.template_name}</span>
                                                </div>
                                                <div className="card-toolbar gap-2">
                                                    {template.is_custom && <span className="badge badge-light-info">Custom</span>}
                                                    {isSelected && <span className="badge badge-light-success">Selected</span>}
                                                </div>
                                            </div>
                                            <div className="card-body p-3">
                                                {/* scaled down preview; sandbox blocks scripts in template html */}
                                                <div className="border rounded overflow-hidden position-relative bg-white" style={{ height: "300px" }}>
                                                    <iframe
                                                        title={template.template_id}
                                                        srcDoc={fillSampleData(template)}
                                                        sandbox=""
                                                        style={{ width: "250%", height: "750px", border: 0, transform: "scale(0.4)", transformOrigin: "0 0", pointerEvents: "none" }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="card-footer d-flex justify-content-between px-5 py-4">
                                                <button className="btn btn-sm btn-light" onClick={() => setPreviewTemplate(template)}>
                                                    <i className="bi bi-eye"></i> Preview
                                                </button>
                                                {write && !isSelected && (
                                                    <button className="btn btn-sm btn-primary" onClick={() => selectTemplate(template)} data-kt-indicator={savingTemplateId === template.template_id ? "on" : "off"} disabled={savingTemplateId !== null}>
                                                        <span className="indicator-label">Use Template</span>
                                                        <span className="indicator-progress">Please wait...
                                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span></span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    :
                    state == 2 ?
                        <div className="text-center py-10">
                            <span className="spinner-border text-primary"></span>
                        </div>
                        :
                        <PageError />
            }

            <Modal show={previewTemplate !== null} onHide={() => setPreviewTemplate(null)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{previewTemplate && previewTemplate.template_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {previewTemplate && (
                        <iframe title="template-preview" srcDoc={fillSampleData(previewTemplate)} sandbox="" style={{ width: "100%", height: "75vh", border: 0 }} />
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default TransactionTemplateSettings;
